import { useEffect, useState, useCallback } from 'react';
import './LiveRates.css';
import { ChevronUp, ChevronDown } from 'lucide-react';
import { getMetalRates, formatCurrencyINR, getStateRateConstraint } from '../services/metalRates';
import { useSeller } from '../contexts/SellerContext';

const POLL_MS = 1000;
const COMPARISON_SNAPSHOT_KEY = 'sellerAllMetalsComparison_v1';

const getPreviousSnapshot = () => {
  try {
    const raw = localStorage.getItem(COMPARISON_SNAPSHOT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const updateSnapshots = (data) => {
  const now = Date.now();
  
  // First save current data as the new comparison snapshot (to be used as previous in next update)
  const snapshotData = {
    timestamp: now,
    gold: {
      central: data.gold?.central,
      india: data.gold?.india,
      local: data.gold?.local,
      RTGS_SALE_RATE: data.gold?.RTGS_SALE_RATE,
      RTGS_PURCHASE_RATE: data.gold?.RTGS_PURCHASE_RATE,
      METAL_SALE_RATE: data.gold?.METAL_SALE_RATE,
      METAL_PURCHASE_RATE: data.gold?.METAL_PURCHASE_RATE,
    },
    silver: {
      central: data.silver?.central,
      india: data.silver?.india,
      local: data.silver?.local,
      RTGS_SALE_RATE: data.silver?.RTGS_SALE_RATE,
      RTGS_PURCHASE_RATE: data.silver?.RTGS_PURCHASE_RATE,
      METAL_SALE_RATE: data.silver?.METAL_SALE_RATE,
      METAL_PURCHASE_RATE: data.silver?.METAL_PURCHASE_RATE,
    },
    platinum: {
      central: data.platinum?.central,
      india: data.platinum?.india,
      local: data.platinum?.local,
      RTGS_SALE_RATE: data.platinum?.RTGS_SALE_RATE,
      RTGS_PURCHASE_RATE: data.platinum?.RTGS_PURCHASE_RATE,
      METAL_SALE_RATE: data.platinum?.METAL_SALE_RATE,
      METAL_PURCHASE_RATE: data.platinum?.METAL_PURCHASE_RATE,
    },
  };
  
  localStorage.setItem(COMPARISON_SNAPSHOT_KEY, JSON.stringify(snapshotData));
};

const RATE_FIELDS = [
  { key: 'RTGS_SALE_RATE', label: 'RTGS Sale', type: 'sale' },
  { key: 'RTGS_PURCHASE_RATE', label: 'RTGS Purchase', type: 'purchase' },
];

const calculateChange = (current, previous) => {
  const curr = Number(current) || 0;
  const prev = Number(previous) > 0 ? Number(previous) : curr;
  const change = curr - prev;

  if (Math.abs(change) < 0.01) {
    return {
      amount: 0,
      percentage: "0.00",
      direction: "flat",
    };
  }

  const percentage = ((change / prev) * 100).toFixed(2);
  return {
    amount: change,
    percentage: Math.abs(Number(percentage)).toFixed(2),
    direction: change > 0 ? "up" : "down",
  };
};

const ChangeIndicator = ({ current, previous }) => {
  const change = calculateChange(current, previous);

  const isNegative = change?.direction === "down";
  const Icon = isNegative ? ChevronDown : ChevronUp;
  const colorClass = isNegative ? "negative" : "positive";

  const amountStr = change && isNegative
    ? `-${formatCurrencyINR(Math.abs(change.amount))}`
    : `+${formatCurrencyINR(Math.abs(change?.amount || 0))}`;

  const pctStr = change && isNegative
    ? `(-${change.percentage}%)`
    : `(+${change?.percentage || "0.00"}%)`;

  return (
    <div className="rate-change-row">
      <span className={`rate-change-amount ${colorClass}`}>{amountStr}</span>
      <span className={`rate-change-pill ${colorClass}`}>
        <Icon size={14} className="rate-change-icon" />
        <span>{pctStr}</span>
      </span>
    </div>
  );
};

const RateCell = ({ current, previous, constraint }) => {
  return (
    <div className="rate-cell-display">
      <div className="rate-main-value">
        {formatCurrencyINR(current)}
        <span className="rate-unit">/g</span>
        {constraint !== undefined && constraint !== null && Number(constraint) !== 0 && (
          <span className="rate-constraint-tag">
            ({Number(constraint) > 0 ? "+" : ""}{Number(constraint).toFixed(2)}%)
          </span>
        )}
      </div>
      <ChangeIndicator current={current} previous={previous} />
    </div>
  );
};

export default function LiveRates({ marketLabel = 'Your State', pollMs = POLL_MS, useLiveApi = true }) {
  const { seller } = useSeller();
  const [data, setData] = useState(null);
  const [constraint, setConstraint] = useState(null);
  const [previousSnapshot, setPreviousSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    try {
      setError(null);
      
      // STEP 1: Get the PREVIOUS snapshot BEFORE fetching new data
      const prevSnap = getPreviousSnapshot();
      
      // STEP 2: Fetch live rates
      const res = await getMetalRates({ market: marketLabel, useLiveApi, state: seller?.state });
      
      // STEP 3: Set the previous snapshot for rendering comparisons
      setPreviousSnapshot(prevSnap);
      
      // STEP 4: Set the current data
      setData(res);
      
      // STEP 5: Fetch state constraints
      const constraints = await getStateRateConstraint(seller?.state);
      setConstraint(constraints);

      // STEP 6: Save new data as snapshot for NEXT cycle
      if (res) updateSnapshots(res);
    } catch (e) {
      console.error('Failed to fetch rates:', e);
      setError('Failed to fetch live rates');
    } finally {
      setLoading(false);
    }
  }, [marketLabel, useLiveApi, seller?.state]);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, pollMs);
    return () => clearInterval(id);
  }, [marketLabel, pollMs, useLiveApi, refresh]);

  const metals = [
    { key: 'gold', label: 'Gold' },
    { key: 'silver', label: 'Silver' },
    { key: 'platinum', label: 'Platinum' },
  ];

  const stateLabel = data?.stateName || seller?.state || 'Your State';

  return (
    <div className="live-rates-page">
      <div className="live-rates">
        <div className="live-rates__header">
          <div className="live-rates__header-left">
            <h3 className="live-rates__title">Live Metal Rates by State</h3>
            <div className="live-rates__market">{stateLabel}</div>
          </div>
          <div className="live-rates__status">
            <span className="live-rates__badge">● Live</span>
            <div className="live-rates__updated">
              {loading ? (
                'Loading…'
              ) : error ? (
                <span className="error">{error}</span>
              ) : (
                `Updated ${new Date().toLocaleTimeString('en-IN', {
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit',
                })}`
              )}
            </div>
          </div>
        </div>

        {loading && !data ? (
          <div className="live-rates__loading">
            <div className="spinner" />
            <p>Fetching latest rates...</p>
          </div>
        ) : (
          <div className="live-rates__table-wrap">
            <table className="live-rates__table">
              <thead>
                <tr>
                  <th className="th-metal">Metal</th>
                  {RATE_FIELDS.map((f) => (
                    <th key={f.key} className={`th-rate th-rate--${f.type}`}>
                      <div className="th-label">{f.label}</div>
                      <div className="th-unit">₹/gram</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {metals.map((metal) => (
                  <tr key={metal.key} className="metal-row">
                    <td data-label="Metal" className="td-metal">
                      <span className="table-metal-name">
                        {metal.label}
                      </span>
                    </td>
                    {RATE_FIELDS.map((field) => {
                      const current = data?.[metal.key]?.[field.key];
                      const previous = previousSnapshot?.[metal.key]?.[field.key];
                      
                      // Get constraint for this metal and rate type
                      const constraintKey = field.key
                        .replace('_RATE', '')
                        .toLowerCase()
                        .replace('rtgs_', 'rtgs')
                        .replace('metal_', 'metal');
                      const constraintValue = constraint?.[metal.key]?.[constraintKey];
                      
                      return (
                        <td key={field.key} data-label={field.label} className={`td-rate td-rate--${field.type}`}>
                          <RateCell 
                            current={current} 
                            previous={previous}
                            constraint={constraintValue}
                          />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
